import { reactive, readonly } from 'vue';

const state = reactive({
  show: false,
  message: '',
  type: 'error',
  title: '',
  typeLabel: '',
  link: '',
  alertcode: '',
});

const queue = [];
const mergeableTypes = new Set(['error', 'warning', 'info']);

function collectMessages(value) {
  if (value === null || value === undefined || value === '') return [];
  if (value instanceof Error) return collectMessages(value.message);

  if (Array.isArray(value)) {
    return value.flatMap((item) => collectMessages(item));
  }

  if (typeof value === 'object') {
    if (value.errors !== undefined) return collectMessages(value.errors);
    if (value.message !== undefined) return collectMessages(value.message);
    if (value.detail !== undefined) return collectMessages(value.detail);
    if (value.error !== undefined) return collectMessages(value.error);

    return Object.values(value).flatMap((item) => collectMessages(item));
  }

  return String(value)
    .split(/\s+\|\s+|\r?\n/)
    .map((message) => message.trim())
    .filter(Boolean);
}

function uniqueMessages(messages) {
  const seen = new Set();
  return messages.filter((message) => {
    const normalized = String(message || '').trim();
    if (!normalized || seen.has(normalized)) return false;
    seen.add(normalized);
    return true;
  });
}

function formatMessages(messages) {
  const unique = uniqueMessages(messages);
  if (unique.length <= 1) return unique[0] || '';
  return unique.map((message) => `- ${message}`).join('\n');
}

function parseFormattedMessage(message) {
  return collectMessages(message).map((item) => String(item).replace(/^-\s*/, '').trim()).filter(Boolean);
}

function normalizePayload(payload, type) {
  if (payload && typeof payload === 'object' && !(payload instanceof Error)) {
    const rawMessage = payload.message ?? payload.detail ?? payload.error ?? payload.errors ?? '';
    return {
      message: formatMessages(collectMessages(rawMessage)),
      type: payload.type ?? type,
      title: payload.title ?? '',
      typeLabel: payload.typeLabel ?? payload.label ?? '',
      link: payload.link ?? '',
      alertcode: payload.alertcode ?? payload.alertCode ?? payload.code ?? '',
    };
  }

  return {
    message: formatMessages(collectMessages(payload)),
    type,
    title: '',
    typeLabel: '',
    link: '',
    alertcode: '',
  };
}

function applyAlert(alert) {
  state.message = formatMessages(parseFormattedMessage(alert.message));
  state.type = alert.type || 'error';
  state.title = alert.title == null ? '' : String(alert.title);
  state.typeLabel = alert.typeLabel == null ? '' : String(alert.typeLabel);
  state.link = alert.link == null ? '' : String(alert.link);
  state.alertcode = alert.alertcode == null ? '' : String(alert.alertcode);
  state.show = true;
}

function canMergeAlerts(currentType, incomingType) {
  return currentType === incomingType && mergeableTypes.has(currentType);
}

function mergeIntoState(alert) {
  state.message = formatMessages([
    ...parseFormattedMessage(state.message),
    ...parseFormattedMessage(alert.message),
  ]);

  if (!state.title && alert.title) state.title = String(alert.title);
  if (!state.typeLabel && alert.typeLabel) state.typeLabel = String(alert.typeLabel);
  if (!state.link && alert.link) state.link = String(alert.link);
  if (!state.alertcode && alert.alertcode) state.alertcode = String(alert.alertcode);
}

function mergeIntoQueue(alert) {
  const lastAlert = queue[queue.length - 1];
  if (!lastAlert || !canMergeAlerts(lastAlert.type, alert.type)) return false;

  lastAlert.message = formatMessages([
    ...parseFormattedMessage(lastAlert.message),
    ...parseFormattedMessage(alert.message),
  ]);
  if (!lastAlert.title && alert.title) lastAlert.title = alert.title;
  if (!lastAlert.typeLabel && alert.typeLabel) lastAlert.typeLabel = alert.typeLabel;
  if (!lastAlert.link && alert.link) lastAlert.link = alert.link;
  if (!lastAlert.alertcode && alert.alertcode) lastAlert.alertcode = alert.alertcode;
  return true;
}

function pullNextAlert() {
  const nextAlert = queue.shift();
  if (!nextAlert) return null;

  while (queue.length && canMergeAlerts(nextAlert.type, queue[0].type)) {
    const mergeAlert = queue.shift();
    nextAlert.message = formatMessages([
      ...parseFormattedMessage(nextAlert.message),
      ...parseFormattedMessage(mergeAlert.message),
    ]);
    if (!nextAlert.title && mergeAlert.title) nextAlert.title = mergeAlert.title;
    if (!nextAlert.typeLabel && mergeAlert.typeLabel) nextAlert.typeLabel = mergeAlert.typeLabel;
    if (!nextAlert.link && mergeAlert.link) nextAlert.link = mergeAlert.link;
    if (!nextAlert.alertcode && mergeAlert.alertcode) nextAlert.alertcode = mergeAlert.alertcode;
  }

  return nextAlert;
}

function openAlert(payload, type = 'error') {
  const alert = normalizePayload(payload, type);

  if (state.show) {
    if (canMergeAlerts(state.type, alert.type)) {
      mergeIntoState(alert);
      return;
    }

    if (mergeIntoQueue(alert)) return;
    queue.push(alert);
    return;
  }

  applyAlert(alert);
}

function closeAlert() {
  state.show = false;

  const nextAlert = pullNextAlert();
  if (nextAlert) {
    setTimeout(() => applyAlert(nextAlert), 180);
  }
}

export function useAlertModal() {
  return {
    alertState: readonly(state),
    openAlert,
    closeAlert,
  };
}

export const alertModal = {
  show: openAlert,
  error: (payload) => openAlert(payload, 'error'),
  success: (payload) => openAlert(payload, 'success'),
  info: (payload) => openAlert(payload, 'info'),
  warning: (payload) => openAlert(payload, 'warning'),
  clear: closeAlert,
  dismiss: closeAlert,
};
